import React from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  ChevronRight,
  CheckCircle2,
  Database,
  Download,
  FileSpreadsheet,
  History,
  LoaderCircle,
	MapPin,
  Package,
  ShieldCheck,
  Tag,
  Trash2,
  Truck,
  Upload,
  UserRound,
  Warehouse,
  X,
} from "lucide-react";
import { invalidateDataManagerStateCache } from "../hooks/dataManagerMutationEffects.js";
import { useImportConfirmationDialog } from "../hooks/useImportConfirmationDialog.js";
import { useImportSubmissionFlow } from "../hooks/useImportSubmissionFlow.js";
import useAccess from "../hooks/useAccess.jsx";
import { convertSollioToOrders, convertUniagToOrders } from "../lib/exportApi.js";
import {
	ACCESS_PERMISSION_KEYS,
  IMPORT_UPLOAD_PERMISSION_BY_FIELD,
  canManageImports as canUseImportReplaceAll,
  canUseOrderImportGuardBypass as canBypassOrderImportGuards,
} from "../lib/accessControl.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import useSourceFilesStorage from "../hooks/useSourceFilesStorage.js";
import { resolvePageSurfaceWidthClassName } from "../utils/pageSurfaceLayout.js";
import {
	buildInitialSollioDeliveryAddressSelections,
  buildSollioDeliveryAddressSelections,
  formatSollioDeliveryAddressOption,
  groupSollioDeliveryAddressOrdersByFamily,
  isSollioDeliveryAddressSelectionComplete,
  normalizeSollioDeliveryAddressChallenge,
  resolveSollioDeliveryAddressDialogTitle,
} from "../utils/sollioDeliveryAddressSelection.js";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import {
  createInitialImportSubmissionState,
  createSollioConversionErrorSubmissionState,
  createUniagConversionErrorSubmissionState,
  SHOW_FORCE_REPLACE_ACTION,
} from "../utils/importFilesPageState.js";
import { isImportActionDisabled } from "../validation/import/importValidations.js";
import ConfirmationModal from "./ConfirmationModal.jsx";
import OrderDuplicateConfirmationDialog from "./OrderDuplicateConfirmationDialog.jsx";
import WorkspacePageHeader from "./WorkspacePageHeader.jsx";
import SollioDeliveryAddressManagerDialog from "./SollioDeliveryAddressManagerDialog.jsx";
import { HoverCard } from "./ResultsPrimitives.jsx";
import { buttonToneClasses, joinClasses, pillToneClasses, shellControlPastilleClassName, statusToneClasses } from "./resultsPrimitives/shared.js";
import "./ImportFilesPage.css";
import "./TraceabilityBanner.css";

const IMPORT_SURFACE_WIDTH_CLASS_NAME = resolvePageSurfaceWidthClassName("import");
const IMPORT_IMPACT_ENTITY_LABELS = {
  orders: "Commandes concernées",
  trucks: "Camions concernés",
  compartments: "Compartiments concernés",
  client_correspondences: "Clients concernés",
  products: "Codes produit concernés",
};
const IMPORT_DIFF_ENTITY_LABELS = {
  orders: {
    added: "Nouvelles commandes importées",
    modified: "Commandes existantes modifiées",
    removed: "Commandes actuelles qui seraient retirées",
  },
  trucks: {
    added: "Nouveaux camions importés",
    modified: "Camions existants modifiés",
    removed: "Camions actuels qui seraient retirés",
  },
  compartments: {
    added: "Nouveaux compartiments importés",
    modified: "Compartiments existants modifiés",
    removed: "Compartiments actuels qui seraient retirés",
  },
  client_correspondences: {
    added: "Nouveaux clients importés",
    modified: "Clients existants modifiés",
    removed: "Clients actuels qui seraient retirés",
  },
  products: {
    added: "Nouveaux codes produit importés",
    modified: "Codes produit existants modifiés",
    removed: "Codes produit actuels qui seraient retirés",
  },
};
const IMPORT_BLOCKING_ENTITY_LABELS = {
  orders: "Commandes du planning touchées",
  trucks: "Camions du planning touchés",
  compartments: "Compartiments utilisés touchés",
  deliveries: "Livraisons manuelles touchées",
  loadings: "Chargements manuels touchés",
};
const IMPORT_HISTORY_SOURCE_LABELS = {
  client_correspondence_import: "Clients",
  product_correspondence_import: "Codes produit",
  compartment_import: "Remorques",
  employee_import: "Chauffeurs",
  order_import: "Commandes",
  truck_import: "Camions",
};
const IMPORT_HISTORY_ACTION_LABELS = {
  import_merge: "Import appliqué",
  import_replace: "Remplacement complet",
};
const IMPORT_FIELD_CONFIGS = [
  {
    description: "Source commandes.",
    field: "orders",
    label: "Commandes",
    targetName: "data/orders.xlsx",
    usageLabel: "Import des commandes",
    Icon: Package,
  },
  {
    description: "Source camions.",
    field: "trucks",
    label: "Camions",
    targetName: "data/trucks.xlsx",
    usageLabel: "Import des camions",
    Icon: Truck,
  },
  {
    description: "Compartiments des remorques.",
    field: "compartments",
    label: "Remorques",
    targetName: "data/Répartition des citernes.xlsx",
    usageLabel: "Import des remorques",
    Icon: Warehouse,
  },
  {
    description: "Fiches chauffeurs : courriel, quart et heures (export Chauffeurs du Data Manager).",
    field: "employees",
    label: "Chauffeurs",
    targetName: "Data Manager > Chauffeurs",
    usageLabel: "Import des chauffeurs",
    Icon: UserRound,
  },
  {
    description: "Clients utilisés par la conversion Sollio.",
    field: "client_correspondences",
    label: "Clients",
    targetName: "Emplacement -> chargement",
    usageLabel: "Import des clients",
    Icon: Database,
  },
  {
    description: "Correspondances Description Sollio vers produit.",
    field: "products",
    label: "Codes produit",
    targetName: "Emplacement -> chargement",
    usageLabel: "Import des codes produit",
    Icon: Tag,
  },
];
const SOURCE_STORAGE_FIELD_CONFIGS = IMPORT_FIELD_CONFIGS;

const PRIMARY_BUTTON_CLASS_NAME = joinClasses([
  "import-primary-action inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border px-3.5 py-2 text-[12px] font-semibold transition",
  buttonToneClasses.optimizer,
]);
const SECONDARY_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border px-3.5 py-2 text-[12px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.ghost,
]);
const CLEAR_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-8 items-center justify-center gap-1 rounded-[4px] border px-2.5 py-1.5 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.ghost,
]);
const CONFIRM_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border px-3.5 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.warning,
]);

const FORCE_REPLACE_BUTTON_CLASS_NAME = joinClasses([
  "inline-flex min-h-9 items-center justify-center gap-1.5 rounded-[4px] border px-3.5 py-2 text-[12px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-60",
  buttonToneClasses.warning,
]);

function buildInitialFilesState() {
  return IMPORT_FIELD_CONFIGS.reduce((state, config) => ({
    ...state,
    [config.field]: null,
  }), {});
}

function buildInitialInputVersions() {
  return IMPORT_FIELD_CONFIGS.reduce((state, config) => ({
    ...state,
    [config.field]: 0,
  }), {});
}

function resolveResultLabel(field) {
  if (field === "sollio") {
    return "Convertisseur Sollio";
  }
  if (field === "uniag") {
    return "Convertisseur Uniag";
  }
  const config = IMPORT_FIELD_CONFIGS.find((candidate) => candidate.field === field);
  return config?.label || field || "Requête";
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function resolveFeedbackToneClassName(tone) {
  if (tone === "neutral") {
    return statusToneClasses.neutral;
  }
  if (tone === "info") {
    return statusToneClasses.info;
  }
  if (tone === "success") {
    return statusToneClasses.success;
  }
  if (tone === "warning") {
    return statusToneClasses.warning;
  }
  if (tone === "danger") {
    return statusToneClasses.danger;
  }
  return pillToneClasses.outline;
}

function formatStoredFileTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "date non disponible";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "date non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
}

function formatStoredFileSize(sizeBytes) {
  if (typeof sizeBytes !== "number" || !Number.isFinite(sizeBytes) || sizeBytes < 0) {
    return "";
  }

  if (sizeBytes < 1024) {
    return `${sizeBytes} o`;
  }

  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(sizeBytes >= 10240 ? 0 : 1)} Ko`;
  }

  return `${(sizeBytes / (1024 * 1024)).toFixed(sizeBytes >= 10 * 1024 * 1024 ? 0 : 1)} Mo`;
}

function formatStoredFileDate(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "—";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsedDate);
}

function formatStoredFileVersion(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "—";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  const versionDateLabel = `v${parsedDate.getFullYear()}.${String(parsedDate.getMonth() + 1).padStart(2, "0")}.${String(parsedDate.getDate()).padStart(2, "0")}`;
  const versionTimeLabel = new Intl.DateTimeFormat("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate);

  return `${versionDateLabel} ${versionTimeLabel}`;
}

function resolveImportHistoryResponseErrorMessage(payload, fallbackMessage) {
  if (typeof payload?.detail === "string" && payload.detail.trim()) {
    return payload.detail.trim();
  }

  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message.trim();
  }

  return fallbackMessage;
}

async function fetchImportHistoryItems() {
  const response = await fetchWithSupabaseAuth("/api/import/history");
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(resolveImportHistoryResponseErrorMessage(payload, "Impossible de charger la traçabilité des imports Excel."));
  }

  return Array.isArray(payload?.items) ? payload.items : [];
}

function resolveImportHistoryErrorMessage(error) {
  const message = typeof error?.message === "string" ? error.message.trim() : "";
  return message || "Impossible de charger la traçabilité des imports Excel.";
}

function formatImportHistoryTimestamp(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "date non disponible";
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return "date non disponible";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsedDate);
}

function resolveImportHistorySourceLabel(entry) {
  const entityKind = typeof entry?.entity_kind === "string" ? entry.entity_kind.trim().toLowerCase() : "";
  return IMPORT_HISTORY_SOURCE_LABELS[entityKind] || entry?.entity_label || "Import Excel";
}

function resolveImportHistoryActionLabel(action) {
  const normalizedAction = typeof action === "string" ? action.trim().toLowerCase() : "";
  return IMPORT_HISTORY_ACTION_LABELS[normalizedAction] || "Import appliqué";
}

function resolveImportHistoryImpactLabel(impactLevel) {
  const normalizedImpactLevel = typeof impactLevel === "string" ? impactLevel.trim().toLowerCase() : "";

  if (normalizedImpactLevel === "breaking") {
    return "Dispatch à revalider";
  }
  if (normalizedImpactLevel === "minor") {
    return "Impact mineur";
  }
  if (normalizedImpactLevel === "none") {
    return "Sans impact";
  }

  return "Impact non précisé";
}

function resolveImportHistoryImpactToneClassName(impactLevel) {
  const normalizedImpactLevel = typeof impactLevel === "string" ? impactLevel.trim().toLowerCase() : "";

  if (normalizedImpactLevel === "breaking") {
    return statusToneClasses.warning;
  }
  if (normalizedImpactLevel === "minor") {
    return statusToneClasses.info;
  }
  if (normalizedImpactLevel === "none") {
    return statusToneClasses.neutral;
  }

  return statusToneClasses.neutral;
}

function resolveStoredFileStatus({
  currentFile,
  errorMessage,
  isSessionReady,
  isStorageAvailable,
  isLoading,
  storageProviderLabel,
  storageAccessReason,
}) {
  if (!isSessionReady) {
    return {
      label: "Session",
      summary: "Vérification de la session avant lecture du fichier courant.",
      toneClassName: statusToneClasses.neutral,
    };
  }

  if (!isStorageAvailable) {
    return {
      label: "Indisponible",
      summary: storageAccessReason || "Le stockage des fichiers courants n'est pas disponible pour cette session.",
      toneClassName: statusToneClasses.warning,
    };
  }

  if (isLoading) {
    return {
      label: "Chargement",
      summary: `Lecture du fichier courant dans ${storageProviderLabel || "le stockage courant"}.`,
      toneClassName: statusToneClasses.neutral,
    };
  }

  if (errorMessage) {
    return {
      label: "Erreur",
      summary: errorMessage,
      toneClassName: statusToneClasses.danger,
    };
  }

  if (currentFile) {
    const sizeLabel = formatStoredFileSize(currentFile.sizeBytes);
    return {
      label: "Disponible",
      summary: `Mis à jour le ${formatStoredFileTimestamp(currentFile.updatedAt)}${sizeLabel ? ` · ${sizeLabel}` : ""}`,
      toneClassName: statusToneClasses.success,
    };
  }

  return {
    label: "Vide",
    summary: "Aucun fichier courant n'est déposé pour cette source.",
    toneClassName: statusToneClasses.neutral,
  };
}

function resolveFeedbackSummary(feedback, impact) {
  if (!feedback) {
    return "Retour d’import disponible.";
  }
  if (feedback?.issue?.code === "import.no_changes") {
    return "Aucun changement";
  }
  if (feedback?.issue?.code === "sollio.conversion_error") {
    return "Conversion Sollio en erreur";
  }
  if (feedback?.issue?.code === "uniag.conversion_error") {
    return "Conversion Uniag en erreur";
  }
  if (feedback?.issue?.code === "import.confirmation_required" || impact?.requires_confirmation) {
    return "Confirmation requise";
  }
  if (feedback?.issue?.code === "import.guard_blocked") {
    return "Import bloqué";
  }
  if (feedback?.issue?.code === "import.rate_limited") {
    return "Import temporairement limité";
  }
  if (feedback.tone === "success" || feedback?.issue?.code === "import.reoptimization_required" || feedback?.issue?.code?.startsWith("import.completed")) {
    return "Import terminé";
  }
  return feedback.message;
}

function resolveImpactBadgeLabel(impact) {
  if (impact?.guard_blocked) {
    return "Import bloqué";
  }
  if (impact?.requires_confirmation) {
    return "Confirmation requise";
  }
  if (impact?.level === "breaking") {
    return "Dispatch à revalider";
  }
  if (impact?.level === "minor") {
    return "Impact mineur";
  }
  return "Impact non précisé";
}

function resolveImpactBadgeClassName(impact) {
  if (impact?.guard_blocked) {
    return statusToneClasses.warning;
  }
  if (impact?.requires_confirmation || impact?.level === "breaking") {
    return statusToneClasses.warning;
  }
  if (impact?.level === "minor") {
    return statusToneClasses.info;
  }
  return statusToneClasses.neutral;
}

function resolveImpactAction(impact, feedbackIssueCode, {
  bypassOrderImportGuards = false,
  canUseOrderImportGuardBypass = false,
  submissionMode = "selection",
} = {}) {
  if (!isPlainObject(impact)) {
    return null;
  }

  if (feedbackIssueCode === "import.backend_rejected") {
    return null;
  }

  if (impact?.guard_blocked || feedbackIssueCode === "import.guard_blocked") {
    if (canUseOrderImportGuardBypass) {
      return {
        label: "Choix requis",
        message: bypassOrderImportGuards
          ? "Forçage actif : confirmez l’import ou revenez à la sélection."
          : "Pour continuer, cochez « Forcer l’import des commandes (temporaire) » en haut de la page.",
        primaryActionDescription: bypassOrderImportGuards
          ? "Relance l’import avec le forçage."
          : "Cochez d’abord « Forcer l’import des commandes (temporaire) » en haut de la page.",
        primaryActionDisabled: !bypassOrderImportGuards,
        primaryActionLabel: bypassOrderImportGuards ? "Confirmer l’import" : "Cochez le forçage d’abord",
      };
    }

    return {
      label: "Choix requis",
      message: "Revenez à la sélection pour corriger le fichier avant un nouvel essai.",
      primaryActionDescription: null,
      primaryActionDisabled: true,
      primaryActionLabel: null,
    };
  }

  const signals = isPlainObject(impact?.signals) ? impact.signals : {};

  if (impact.requires_confirmation) {
    return {
      label: "Choix requis",
      // Remplacement complet : les sources sont remplacées et le planning courant lié est effacé, rien n'est appliqué au planning.
      message: submissionMode === "force"
        ? "Confirmez l’import pour remplacer les sources correspondantes et effacer le planning courant lié, ou revenez à la sélection pour modifier les fichiers sans rien écrire."
        : "Confirmez l’import pour appliquer les nouvelles sources au planning actif, ou revenez à la sélection pour modifier les fichiers sans rien écrire.",
      primaryActionDescription: "Applique les nouveaux fichiers sur le serveur en conservant le planning actif, puis force le rechargement des vues dépendantes.",
      primaryActionLabel: "Confirmer l’import",
    };
  }

  if (signals.snapshot_active) {
    return {
      label: "Étape suivante",
      message: feedbackIssueCode === "import.reoptimization_required"
        ? "Ouvrez le Dispatch, puis « Optimiser la flotte »."
        : "Ouvrez le Dispatch, puis « Mise à jour ».",
    };
  }

  return null;
}

function buildImpactChangeValuesByEntity(changeEntries) {
  return (Array.isArray(changeEntries) ? changeEntries : []).reduce((entries, entry) => {
    const entityKey = typeof entry?.key === "string"
      ? entry.key.split("-")[0]
      : "";
    if (!entityKey) {
      return entries;
    }

    const values = normalizeImpactValues(entry?.values);
    if (values.length === 0) {
      return entries;
    }

    entries[entityKey] = [...(entries[entityKey] || []), ...values];
    return entries;
  }, {});
}

function normalizeImpactValueSet(values) {
  return [...new Set(normalizeImpactValues(values))].sort();
}

function filterRedundantBlockingEntries(blockingEntries, changeEntries) {
  const changeValuesByEntity = buildImpactChangeValuesByEntity(changeEntries);

  return (Array.isArray(blockingEntries) ? blockingEntries : []).reduce((entries, entry) => {
    const relatedChangeValueSet = new Set(normalizeImpactValueSet(changeValuesByEntity[entry.key] || []));
    if (relatedChangeValueSet.size === 0) {
      entries.push(entry);
      return entries;
    }

    const seenValues = new Set();
    const remainingValues = normalizeImpactValues(entry.values).filter((value) => {
      if (seenValues.has(value) || relatedChangeValueSet.has(value)) {
        return false;
      }

      seenValues.add(value);
      return true;
    });

    if (remainingValues.length === 0) {
      return entries;
    }

    entries.push({
      ...entry,
      values: remainingValues,
    });
    return entries;
  }, []);
}

function isGuardBlockedResult({ feedback, result }) {
  return Boolean(
    feedback?.issue?.code === "import.guard_blocked"
      && result?.status === "error"
  );
}

function resolveImportResultStatusState({ feedback, impact, result }) {
  if (result?.status === "unchanged" || (feedback?.issue?.code === "import.no_changes" && result?.status === "success")) {
    return {
      className: statusToneClasses.neutral,
      label: "Aucun changement",
    };
  }

  if (result?.status === "success") {
    return {
      className: statusToneClasses.success,
      label: "Succès",
    };
  }

  if (isConfirmationPendingResult({ feedback, impact, result })) {
    return {
      className: statusToneClasses.warning,
      label: "À confirmer",
    };
  }

  if (isGuardBlockedResult({ feedback, result })) {
    return {
      className: statusToneClasses.warning,
      label: "Bloqué",
    };
  }

  return {
    className: statusToneClasses.danger,
    label: "Erreur",
  };
}

function normalizeImpactValues(values) {
  return Array.isArray(values)
    ? values.filter((value) => typeof value === "string" && value.trim())
    : [];
}

function buildImpactEntityEntries(impact) {
  const entities = isPlainObject(impact?.entities) ? impact.entities : {};
  return Object.entries(IMPORT_IMPACT_ENTITY_LABELS).reduce((entries, [key, label]) => {
    const values = normalizeImpactValues(entities[key]);
    if (values.length === 0) {
      return entries;
    }
    entries.push({ key, label, values });
    return entries;
  }, []);
}

function buildImpactChangeEntries(impact) {
  const diffEntities = isPlainObject(impact?.diff_entities) ? impact.diff_entities : {};
  const entries = Object.entries(IMPORT_DIFF_ENTITY_LABELS).reduce((result, [entityKey, labels]) => {
    const entityDiff = isPlainObject(diffEntities[entityKey]) ? diffEntities[entityKey] : {};

    ["added", "modified", "removed"].forEach((changeKey) => {
      const values = normalizeImpactValues(entityDiff[changeKey]);
      if (values.length === 0) {
        return;
      }

      result.push({
        key: `${entityKey}-${changeKey}`,
        label: labels[changeKey],
        values,
      });
    });

    return result;
  }, []);

  return entries.length > 0 ? entries : buildImpactEntityEntries(impact);
}

function hasImpactRemovals(changeEntries) {
  return Array.isArray(changeEntries) && changeEntries.some((entry) => typeof entry?.key === "string" && entry.key.endsWith("-removed"));
}

function buildImpactBlockingEntries(impact) {
  const blockingEntities = isPlainObject(impact?.blocking_entities) ? impact.blocking_entities : {};
  return Object.entries(IMPORT_BLOCKING_ENTITY_LABELS).reduce((entries, [key, label]) => {
    const values = normalizeImpactValues(blockingEntities[key]);
    if (values.length === 0) {
      return entries;
    }

    entries.push({ key, label, values });
    return entries;
  }, []);
}

function isConfirmationPendingResult({ feedback, impact, result }) {
  return Boolean(
    impact?.requires_confirmation
      && feedback?.issue?.code === "import.confirmation_required"
      && typeof result?.message === "string"
      && result.message.startsWith("Confirmation requise avant écriture"),
  );
}

function ImportButtonHoverCardContent({
  actionLabel,
  description,
  disabledDescription,
  isSubmitting,
  selectedFileCount,
}) {
  const reason = isSubmitting
    ? "Un import est déjà en cours. Attendez la réponse du serveur avant de relancer l'action."
    : selectedFileCount > 0
      ? description
      : disabledDescription;
  const title = isSubmitting
    ? `${actionLabel} temporairement verrouillé`
    : `${actionLabel} ${selectedFileCount > 0 ? "prêt" : "désactivé"}`;

  return (
    <div className="grid gap-2">
      <p className="text-[0.72rem] font-black uppercase tracking-[0.16em] text-slate-500">Action</p>
      <p className="text-sm font-semibold leading-6 text-slate-950">
        {title}
      </p>
      <p className="text-sm leading-6 text-slate-600">{reason}</p>
      {!isSubmitting ? (
        <p className="text-sm leading-6 text-slate-600">Sélection actuelle: {selectedFileCount} fichier{selectedFileCount > 1 ? "s" : ""} prêt{selectedFileCount > 1 ? "s" : ""}.</p>
      ) : null}
    </div>
  );
}

function ActionHoverCardContent({ eyebrow = "Action", title, description, detail = null }) {
  return (
    <div className="grid gap-1.5">
      <p className="text-[0.68rem] font-black uppercase tracking-[0.16em] text-slate-500">{eyebrow}</p>
      <p className="text-sm font-semibold leading-6 text-slate-950">{title}</p>
      <p className="text-sm leading-6 text-slate-600">{description}</p>
      {detail ? <p className="text-[12px] leading-5 text-slate-500">{detail}</p> : null}
    </div>
  );
}

function StoredSourceRow({
  canManageSourceFilesByField,
  config,
  isSessionReady,
  isStorageAvailable,
  onDeleteCurrentFile,
  onDownloadCurrentFile,
  onUploadCurrentFile,
  sourceFileState,
  storageProviderLabel,
  storageAccessReason,
}) {
  const currentFile = sourceFileState?.currentFile ?? null;
  const feedback = sourceFileState?.feedback ?? null;
  const hasCurrentFile = Boolean(currentFile);
  const canManageCurrentSourceFile = canManageSourceFilesByField?.[config.field] === true;
  const isBusy = Boolean(
    sourceFileState?.isDeleting
      || sourceFileState?.isDownloading
      || sourceFileState?.isLoading
      || sourceFileState?.isUploading,
  );
  const status = resolveStoredFileStatus({
    currentFile,
    errorMessage: sourceFileState?.errorMessage,
    isLoading: Boolean(sourceFileState?.isLoading),
    isSessionReady,
    isStorageAvailable,
    storageProviderLabel,
    storageAccessReason,
  });
  const uploadLabel = hasCurrentFile
    ? "Remplacer le fichier courant"
    : "Uploader un nouveau fichier";
  const fileLabel = hasCurrentFile ? currentFile.downloadFileName : "Aucun fichier courant";
  const versionLabel = hasCurrentFile ? formatStoredFileVersion(currentFile.updatedAt) : "—";
  const updatedAtLabel = hasCurrentFile ? formatStoredFileDate(currentFile.updatedAt) : "—";

  const handleUploadChange = (event) => {
    const nextFile = event.target.files?.[0] ?? null;
    event.target.value = "";
    if (!nextFile) {
      return;
    }
    onUploadCurrentFile(config.field, nextFile);
  };

  return (
    <>
      <tr className="border-t border-slate-200/80 first:border-t-0">
        <td data-label="Fichier" className="px-3 py-2 align-top text-[11px] text-slate-700">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-slate-950">{fileLabel}</span>
              <span className={joinClasses([
                "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[10px] font-semibold",
                status.toneClassName,
              ])}>
                {status.label}
              </span>
            </div>
            <p className="mt-0.5 text-[10px] leading-3.5 text-slate-500">{status.summary}</p>
          </div>
        </td>
        <td data-label="Usage" className="px-3 py-2 align-top text-[11px] text-slate-700">{config.usageLabel}</td>
        <td data-label="Version" className="px-3 py-2 align-top">
          <span className="inline-flex items-center rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-0.75 text-[10px] font-semibold text-slate-700">
            {versionLabel}
          </span>
        </td>
        <td data-label="Mis à jour" className="px-3 py-2 align-top text-[11px] text-slate-700">{updatedAtLabel}</td>
        <td data-label="Action" className="px-3 py-2 align-top">
          <div className="import-storage-actions">
            <button
              type="button"
              disabled={!isStorageAvailable || !hasCurrentFile || isBusy}
              onClick={() => onDownloadCurrentFile(config.field)}
              className={CLEAR_BUTTON_CLASS_NAME}
            >
              {sourceFileState?.isDownloading ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
              {sourceFileState?.isDownloading ? "Téléchargement…" : "Télécharger"}
            </button>

            {canManageCurrentSourceFile ? (
              <label
                className={joinClasses([
                  "import-storage-upload",
                  CLEAR_BUTTON_CLASS_NAME,
                  !isStorageAvailable || isBusy ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                ])}
                aria-disabled={!isStorageAvailable || isBusy}
              >
                {sourceFileState?.isUploading ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Upload size={14} aria-hidden="true" />}
                {sourceFileState?.isUploading ? "Envoi…" : uploadLabel}
                <input
                  type="file"
                  accept=".xls,.xlsx"
                  disabled={!isStorageAvailable || isBusy}
                  onChange={handleUploadChange}
                  className="sr-only"
                />
              </label>
            ) : null}

            {canManageCurrentSourceFile && hasCurrentFile ? (
              <button
                type="button"
                disabled={!isStorageAvailable || isBusy}
                onClick={() => onDeleteCurrentFile(config.field)}
                className="inline-flex min-h-9 items-center justify-center gap-1 rounded-[4px] border border-danger bg-white px-2.5 py-1.5 text-[11px] font-semibold text-danger transition hover:bg-danger-soft disabled:cursor-not-allowed disabled:text-slate-400"
              >
                {sourceFileState?.isDeleting ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Trash2 size={14} aria-hidden="true" />}
                {sourceFileState?.isDeleting ? "Suppression…" : "Supprimer"}
              </button>
            ) : null}
          </div>
        </td>
      </tr>

      {feedback?.message ? (
        <tr className="border-t border-slate-100 bg-slate-50/70">
          <td data-label="État" className="px-3 pb-2 pt-0" colSpan={5}>
            <p className={joinClasses([
              "rounded-[4px] border px-2.5 py-1.5 text-[10.5px] leading-4",
              resolveFeedbackToneClassName(feedback.tone),
            ])}>
              {feedback.message}
            </p>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function ImportZone({
  config,
  file,
  inputVersion,
  isDisabled = false,
  disabledReason = "",
  isSubmitting,
  onClear,
  onSelect,
}) {
  const Icon = config.Icon;
  const inputId = `${config.field}-file-input-${inputVersion}`;
  const isSelectionDisabled = isSubmitting || isDisabled;
  const clearDescription = disabledReason
    ? disabledReason
    : file
      ? "Retire ce fichier de la sélection en cours sans modifier les autres zones."
      : "Sélectionnez d'abord un fichier dans cette zone pour activer le bouton Effacer.";

  return (
    <article className="import-card import-source-card">
      <div className="import-card-header">
        <span className="import-card-icon" aria-hidden="true"><Icon size={24} /></span>
        <div className="min-w-0">
          <p className="import-eyebrow">Source Excel</p>
          <h3 className="import-source-title">{config.label}</h3>
        </div>
      </div>

      <div className="import-file-field">
        <label
          htmlFor={inputId}
          className={joinClasses([
            "import-file-picker",
            isSelectionDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
          ])}
        >
          <span className="import-file-trigger">
            Choisir un fichier
          </span>
          <span
            title={file ? file.name : undefined}
            className={joinClasses([
              "import-file-name",
              file ? "text-slate-700" : "text-slate-400",
            ])}
          >
            {file ? file.name : "Aucun fichier sélectionné"}
          </span>
        </label>
        <input
          id={inputId}
          key={`${config.field}-${inputVersion}`}
          type="file"
          aria-label={`Fichier ${config.label}`}
          accept=".xls,.xlsx"
          disabled={isSelectionDisabled}
          onChange={(event) => onSelect(config.field, event.target.files?.[0] ?? null)}
          className="sr-only"
        />
        {disabledReason ? (
          <p className="text-[10.5px] leading-4 text-slate-500">{disabledReason}</p>
        ) : null}
      </div>

      {file ? (
        <div className="import-source-actions">
          <div className="min-w-0 flex-1">
            {file ? (
              <span className={joinClasses([
                "inline-flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-semibold",
                statusToneClasses.info,
              ])}>
                <FileSpreadsheet size={12} aria-hidden="true" />
                Prêt
              </span>
            ) : (
              null
            )}
          </div>

          <HoverCard
            placement="bottom-end"
            content={(
              <ActionHoverCardContent
                eyebrow="Sélection"
                title={file ? `Retirer ${config.label}` : `Aucun fichier pour ${config.label}`}
                description={clearDescription}
                detail={file ? file.name : null}
              />
            )}
          >
            <span className="inline-flex">
              <button
                type="button"
                disabled={isSelectionDisabled || !file}
                onClick={() => onClear(config.field)}
                className={CLEAR_BUTTON_CLASS_NAME}
              >
                <X size={16} aria-hidden="true" />
                Effacer
              </button>
            </span>
          </HoverCard>
        </div>
      ) : null}
    </article>
  );
}

function SeparateConversionPanel({
  badgeLabel,
  feedback,
  file,
  fileLabel,
  inputKeyPrefix,
  inputVersion,
  isConverting,
	manageActionLabel = "",
	onManage,
  onClear,
  onConvert,
  onSelect,
  title,
}) {
  const inputId = `${inputKeyPrefix}-file-input-${inputVersion}`;

  return (
    <article className="import-card import-converter">
      <div className="import-section-heading">
        <div className="import-card-header">
          <span className="import-card-icon" aria-hidden="true"><FileSpreadsheet size={24} /></span>
          <div className="min-w-0">
            <p className="import-eyebrow">Convertisseur séparé</p>
            <h2 className="import-card-title">{title}</h2>
          </div>
        </div>

        {file ? (
          <span className={joinClasses([
            "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[9px] font-semibold",
            pillToneClasses.soft,
          ])}>
            {badgeLabel}
          </span>
        ) : null}
      </div>

      <div className="import-converter-body">
        <div className="import-file-field">
          <span className="import-field-label">{fileLabel}</span>
          <label
            htmlFor={inputId}
            className={joinClasses([
              "import-file-picker",
              isConverting ? "cursor-not-allowed opacity-60" : "cursor-pointer",
            ])}
          >
            <span className="import-file-trigger">
              Choisir un fichier
            </span>
            <span
              title={file ? file.name : undefined}
              className={joinClasses([
                "import-file-name",
                file ? "text-slate-700" : "text-slate-400",
              ])}
            >
              {file ? file.name : "Aucun fichier sélectionné"}
            </span>
          </label>
          <input
            id={inputId}
            key={`${inputKeyPrefix}-${inputVersion}`}
            type="file"
            aria-label={fileLabel}
            accept=".xls,.xlsx"
            disabled={isConverting}
            onChange={(event) => onSelect(event.target.files?.[0] ?? null)}
            className="sr-only"
          />
        </div>

        <div className="import-converter-actions">
			{manageActionLabel && typeof onManage === "function" ? (
				<button
					type="button"
					disabled={isConverting}
					onClick={onManage}
					className={SECONDARY_BUTTON_CLASS_NAME}
				>
					<MapPin size={15} aria-hidden="true" />
					{manageActionLabel}
				</button>
			) : null}
          {file ? (
            <button
              type="button"
              disabled={!file || isConverting}
              onClick={onClear}
              className={CLEAR_BUTTON_CLASS_NAME}
            >
              <X size={16} aria-hidden="true" />
              Effacer
            </button>
          ) : null}

          {file ? (
            <button
              type="button"
              disabled={!file || isConverting}
              onClick={onConvert}
              className={PRIMARY_BUTTON_CLASS_NAME}
            >
              {isConverting ? <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> : <Download size={16} aria-hidden="true" />}
              {isConverting ? "Conversion…" : "Convertir et télécharger"}
            </button>
          ) : null}
        </div>
      </div>

        {file ? (
          <span className={joinClasses([
            "inline-flex items-center gap-1 rounded-[4px] px-2 py-1 text-[10px] font-semibold",
            statusToneClasses.info,
          ])}>
            <FileSpreadsheet size={12} aria-hidden="true" />
            Fichier prêt
          </span>
        ) : null}

      {feedback?.message ? (
        <p className={joinClasses([
          "mt-2 rounded-[4px] border px-2.5 py-1.5 text-[11px] leading-4",
          resolveFeedbackToneClassName(feedback.tone),
        ])}>
          {feedback.message}
        </p>
      ) : null}
    </article>
  );
}

function SollioConversionPanel(props) {
  return (
    <SeparateConversionPanel
      {...props}
      badgeLabel="Sollio - Commandes"
      fileLabel="Fichier Sollio"
      inputKeyPrefix="sollio"
      title="Sollio vers Commandes"
    />
  );
}

function UniagConversionPanel(props) {
  return (
    <SeparateConversionPanel
      {...props}
      badgeLabel="Uniag - Commandes"
      fileLabel="Fichier Uniag"
      inputKeyPrefix="uniag"
      title="Uniag vers Commandes"
    />
  );
}

function SollioDeliveryAddressSelectionDialog({
  challenge,
  isConverting = false,
  isOpen = false,
  selections = {},
  onCancel,
  onClose,
  onConfirm,
  onSelectionChange,
}) {
  const closeButtonRef = React.useRef(null);
  const familyTabButtonRefs = React.useRef([]);
  const titleId = React.useId();
  const descriptionId = React.useId();
  const familyTabsId = React.useId().replace(/:/g, "");
  const [activeFamilyKey, setActiveFamilyKey] = React.useState("");
  const familyGroups = React.useMemo(
    () => groupSollioDeliveryAddressOrdersByFamily(challenge, selections),
    [challenge, selections],
  );
  const initialFamilyGroup = familyGroups.find((group) => !group.isComplete) || familyGroups[0] || null;
  const activeFamilyGroup = familyGroups.find((group) => group.familyKey === activeFamilyKey)
    || initialFamilyGroup;
  const activeFamilyIndex = activeFamilyGroup
    ? familyGroups.findIndex((group) => group.familyKey === activeFamilyGroup.familyKey)
    : -1;
  const hasMultipleFamilies = familyGroups.length > 1;

  const handleClose = React.useCallback(() => {
    setActiveFamilyKey("");
    if (typeof onClose === "function") {
      onClose();
    }
  }, [onClose]);

  const handleCancel = React.useCallback(() => {
    setActiveFamilyKey("");
    if (typeof onCancel === "function") {
      onCancel();
    }
  }, [onCancel]);

  const handleFamilyTabKeyDown = React.useCallback((event, currentIndex) => {
    if (familyGroups.length < 2) {
      return;
    }

    let nextIndex = null;
    if (event.key === "ArrowRight") {
      nextIndex = (currentIndex + 1) % familyGroups.length;
    } else if (event.key === "ArrowLeft") {
      nextIndex = (currentIndex - 1 + familyGroups.length) % familyGroups.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = familyGroups.length - 1;
    }

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    setActiveFamilyKey(familyGroups[nextIndex].familyKey);
    familyTabButtonRefs.current[nextIndex]?.focus();
  }, [familyGroups]);

  React.useEffect(() => {
    if (!isOpen) {
      setActiveFamilyKey("");
      return;
    }

    setActiveFamilyKey((currentFamilyKey) => {
      if (familyGroups.some((group) => group.familyKey === currentFamilyKey)) {
        return currentFamilyKey;
      }
      return (familyGroups.find((group) => !group.isComplete) || familyGroups[0])?.familyKey || "";
    });
  }, [familyGroups, isOpen]);

  React.useEffect(() => {
    if (!isOpen || typeof document === "undefined") {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    const frameId = typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame(() => closeButtonRef.current?.focus())
      : null;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      if (frameId !== null && typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [handleClose, isOpen]);

  if (!isOpen || !challenge || !Array.isArray(challenge.orders)) {
    return null;
  }

  const isComplete = isSollioDeliveryAddressSelectionComplete(challenge, selections);
  const dialogTitle = resolveSollioDeliveryAddressDialogTitle(challenge);
  const dialogContent = (
    <div className="fixed inset-0 z-modal isolate" data-sollio-delivery-address-selection-dialog="true">
      <div
        className="absolute inset-0 overflow-y-auto bg-slate-950/45 px-3 py-3"
        onMouseDown={(event) => {
          if (!event.target?.closest?.('[role="dialog"]')) {
            handleClose();
          }
        }}
      >
        <div className="flex min-h-full items-center justify-center">
          <section
            aria-describedby={descriptionId}
            aria-labelledby={titleId}
            aria-modal="true"
            className={joinClasses([
              "grid max-h-[calc(100dvh-1.5rem)] w-full max-w-[min(62rem,calc(100vw-1.5rem))] overflow-hidden rounded-[4px] border border-black/6 bg-white shadow-[0_28px_64px_rgba(15,23,42,0.18)]",
              hasMultipleFamilies
                ? "grid-rows-[auto_auto_minmax(0,1fr)_auto]"
                : "grid-rows-[auto_minmax(0,1fr)_auto]",
            ])}
            role="dialog"
          >
            <header className="relative flex shrink-0 items-start justify-center border-b border-slate-200 bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(255,255,255,0.98))] px-3 py-1.5">
              <div className="w-full min-w-0 px-10 text-center">
                <p className="m-0 flex flex-wrap items-center justify-center text-[0.58rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
                  Convertisseur Sollio
                </p>
                <h2 id={titleId} className="m-0 text-[1.35rem] font-semibold leading-6 tracking-[-0.035em] text-slate-950">
                  {dialogTitle}
                </h2>
                <p className="m-0 text-[0.75rem] font-medium leading-4 text-slate-700">
                  Sélectionner la ville de livraison
                </p>
                <p id={descriptionId} className="m-0 text-[0.68rem] leading-4 text-slate-600">
                  {challenge.message || "Choisissez une correspondance pour chaque nouvelle commande affichée avant de générer le fichier."}
                </p>
				{challenge.reused_selection_count > 0 ? (
					<p className="mx-auto mt-1 w-fit rounded-[4px] border border-[var(--color-border-success-subtle)] bg-accent-soft px-2 py-0.5 text-[0.66rem] font-semibold text-[var(--color-shell-action-soft-ink)]">
						{challenge.reused_selection_count} adresse(s) déjà mémorisée(s) réutilisée(s) automatiquement; {challenge.unresolved_selection_count} commande(s) à compléter.
					</p>
				) : null}
              </div>

              <button
                ref={closeButtonRef}
                aria-label="Fermer la sélection des villes de livraison"
                className={joinClasses([
                  "absolute right-3 top-2.5 inline-flex min-h-8 shrink-0 items-center justify-center gap-1 rounded-[4px] border px-2.5 py-1.5 text-[11px] font-semibold transition",
                  buttonToneClasses.ghost,
                ])}
                onClick={handleClose}
                type="button"
              >
                <X aria-hidden="true" size={14} />
                Fermer
              </button>
            </header>

            {hasMultipleFamilies ? (
              <div className="shrink-0 overflow-x-auto overflow-y-hidden border-b border-slate-200 bg-slate-50/80 px-3 py-1.5">
                <div
                  aria-label="Familles de destination"
                  aria-orientation="horizontal"
                  className="flex min-w-max items-center gap-1"
                  role="tablist"
                >
                  {familyGroups.map((group, groupIndex) => {
                    const isActive = group.familyKey === activeFamilyGroup?.familyKey;
                    const commandLabel = group.totalCount > 1 ? "commandes" : "commande";
                    const statusLabel = group.isBlocked
                      ? "correspondance indisponible"
                      : (group.isComplete ? "complété" : "incomplet");

                    return (
                      <button
                        ref={(node) => {
                          familyTabButtonRefs.current[groupIndex] = node;
                        }}
                        aria-controls={`${familyTabsId}-family-panel-${groupIndex}`}
                        aria-label={`${group.destinationLabel}, ${group.completedCount} sur ${group.totalCount} ${commandLabel}, ${statusLabel}`}
                        aria-selected={isActive}
                        className={joinClasses([
                          "inline-flex min-h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] border px-2.5 py-1 text-[0.68rem] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1",
                          isActive
                            ? "border-slate-300 bg-white text-slate-950 shadow-sm"
                            : "border-transparent text-slate-600 hover:border-slate-200 hover:bg-white/80 hover:text-slate-900",
                        ])}
                        data-sollio-delivery-address-family-tab={group.familyKey}
                        id={`${familyTabsId}-family-tab-${groupIndex}`}
                        key={group.familyKey}
                        onClick={() => setActiveFamilyKey(group.familyKey)}
                        onKeyDown={(event) => handleFamilyTabKeyDown(event, groupIndex)}
                        role="tab"
                        tabIndex={isActive ? 0 : -1}
                        type="button"
                      >
                        <span>{group.destinationLabel}</span>
                        <span
                          className={joinClasses([
                            "inline-flex min-w-8 items-center justify-center rounded-[4px] px-1.5 py-0.5 text-[0.62rem] font-extrabold tabular-nums",
                            isActive ? "bg-slate-100 text-slate-700" : "bg-white text-slate-500",
                          ])}
                        >
                          {group.completedCount}/{group.totalCount}
                        </span>
                        {group.isBlocked ? (
                          <span
                            className="inline-flex items-center text-amber-700"
                            title="Aucune correspondance disponible pour au moins une commande"
                          >
                            <AlertTriangle aria-hidden="true" size={13} />
                            <span className="sr-only">Correspondance indisponible</span>
                          </span>
                        ) : (group.isComplete ? (
                          <CheckCircle2 aria-hidden="true" className="text-accent" size={13} />
                        ) : null)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <div
              aria-labelledby={hasMultipleFamilies && activeFamilyIndex >= 0
                ? `${familyTabsId}-family-tab-${activeFamilyIndex}`
                : undefined}
              className="min-h-0 overflow-y-auto px-3 py-1.5"
              data-sollio-delivery-address-family-panel={activeFamilyGroup?.familyKey}
              id={hasMultipleFamilies && activeFamilyIndex >= 0
                ? `${familyTabsId}-family-panel-${activeFamilyIndex}`
                : undefined}
              role={hasMultipleFamilies ? "tabpanel" : undefined}
            >
              <div className="grid gap-1">
                {activeFamilyGroup?.entries.map(({ order, orderKey }) => {
                  const orderOptions = Array.isArray(order.options) ? order.options : [];

                  return (
                    <article
                      className="grid gap-1 rounded-[4px] border border-slate-200 bg-slate-50/65 px-2.5 py-1.5 min-[900px]:grid-cols-[minmax(0,1.35fr)_minmax(19rem,1fr)] min-[900px]:items-end"
                      data-sollio-delivery-address-order={orderKey}
                      key={orderKey}
                    >
                      <div className="grid gap-0.5">
                        <p className="m-0 text-[0.58rem] font-semibold uppercase tracking-[0.13em] text-slate-500">
                          Commande · ligne {order.source_row_number}
                        </p>
                        <dl className="grid grid-cols-2 gap-x-2 text-[0.66rem] leading-3.5 min-[720px]:grid-cols-3 min-[900px]:grid-cols-[minmax(8.75rem,1.55fr)_minmax(7.5rem,0.75fr)_minmax(0,0.9fr)]">
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Destination</dt><dd className="truncate font-semibold text-slate-800" title={order.source_destination}>{order.source_destination}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Bon</dt><dd className="truncate font-semibold text-slate-800">{order.purchase_order}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Date</dt><dd className="truncate font-semibold text-slate-800">{order.delivery_date}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Produit</dt><dd className="truncate font-semibold text-slate-800" title={order.product}>{order.product}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Quantité</dt><dd className="truncate font-semibold text-slate-800">{order.quantity_tons}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1"><dt className="shrink-0 text-slate-500">Silo</dt><dd className="truncate font-semibold text-slate-800">{order.silo}</dd></div>
                          <div className="flex min-w-0 items-baseline gap-1 rounded-[4px] bg-sky-50/90 px-1 py-px ring-1 ring-inset ring-sky-200/80"><dt className="shrink-0 font-semibold text-sky-700">Ville source</dt><dd className="min-w-0 truncate font-bold text-sky-950" title={order.source_city}>{order.source_city}</dd></div>
                        </dl>
                      </div>

                      <label className="grid gap-0.5 text-[0.6rem] font-semibold uppercase tracking-[0.11em] text-slate-500">
                        Correspondance de livraison
                        <select
                          aria-label={`Correspondance de livraison pour la ligne ${order.source_row_number}`}
                          className="min-h-7 w-full rounded-[4px] border border-slate-200 bg-white px-2.5 py-0.5 text-[0.7rem] font-medium normal-case tracking-normal text-slate-800 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={isConverting || orderOptions.length === 0}
                          onChange={(event) => onSelectionChange?.(orderKey, event.target.value)}
                          required
                          value={selections[orderKey] == null ? "" : String(selections[orderKey])}
                        >
                          <option value="">
                            {orderOptions.length === 0
                              ? "Aucune correspondance disponible dans le Data Manager"
                              : "Sélectionner une correspondance"}
                          </option>
                          {orderOptions.map((option, optionIndex) => (
                            <option
                              key={`${orderKey}-${String(option.correspondence_id)}-${optionIndex}`}
                              value={option.correspondence_id}
                            >
                              {formatSollioDeliveryAddressOption(option)}
                            </option>
                          ))}
                        </select>
                        {orderOptions.length === 0 ? (
                          <span className="text-[0.64rem] font-medium normal-case tracking-normal text-amber-700">
                            Aucune correspondance disponible dans le Data Manager.
                          </span>
                        ) : null}
                      </label>
                    </article>
                  );
                })}
              </div>
            </div>

            <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50/55 px-3 py-1.5">
              <p className="m-0 text-[0.66rem] text-slate-500">
                {isComplete ? "Toutes les commandes ont une correspondance." : "Une correspondance est requise pour chaque commande."}
              </p>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <button
                  className={SECONDARY_BUTTON_CLASS_NAME}
                  disabled={isConverting}
                  onClick={handleCancel}
                  type="button"
                >
                  Annuler
                </button>
                <button
                  className={PRIMARY_BUTTON_CLASS_NAME}
                  disabled={!isComplete || isConverting}
                  onClick={onConfirm}
                  type="button"
                >
                  {isConverting ? <LoaderCircle size={14} className="animate-spin" aria-hidden="true" /> : <Download size={14} aria-hidden="true" />}
                  {isConverting ? "Génération…" : "Générer et télécharger"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      </div>
    </div>
  );

  const portalRoot = getReactPortalRoot();
  if (portalRoot && typeof HTMLElement !== "undefined" && portalRoot instanceof HTMLElement) {
    return createPortal(dialogContent, portalRoot);
  }

  return dialogContent;
}

function StoredSourcesSection({
  canManageSourceFilesByField,
  isSessionReady,
  isStorageAvailable,
  onDeleteCurrentFile,
  onDownloadCurrentFile,
  onUploadCurrentFile,
  sourceStatesByField,
  storageProviderLabel,
  storageAccessReason,
}) {
  const [isCollapsed, setIsCollapsed] = React.useState(true);
  const hasAnyManageAccess = Object.values(canManageSourceFilesByField || {}).some((value) => value === true);

  return (
    <article className="import-card import-storage">
      <div className="import-section-heading">
        <div className="import-card-header import-storage-heading">
          <button
            type="button"
            onClick={() => setIsCollapsed((current) => !current)}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Déplier les fichiers sources courants" : "Replier les fichiers sources courants"}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
          >
            <ChevronRight size={13} className={isCollapsed ? "" : "rotate-90"} aria-hidden="true" />
          </button>
          <span className="import-card-icon" aria-hidden="true"><Database size={24} /></span>
          <div className="min-w-0">
            <p className="import-eyebrow">Fichiers sources courants</p>
            <h2 className="import-card-title">Sources disponibles au téléchargement</h2>
            {!isCollapsed ? (
              <p className="import-description">
                Cette zone est séparée de l’import local pour distinguer clairement les fichiers déjà stockés dans {storageProviderLabel || "le stockage courant"} des fichiers que vous êtes en train de préparer pour un envoi.
              </p>
            ) : null}
          </div>
        </div>

        <span className={joinClasses([
          "inline-flex items-center rounded-[4px] px-2 py-0.5 text-[9px] font-semibold",
          pillToneClasses.soft,
        ])}>
          {storageProviderLabel || "Stockage courant"}
        </span>
      </div>

      {!isCollapsed ? (
        <>
          {isStorageAvailable && !hasAnyManageAccess ? (
            <p className="mt-2 text-[10.5px] leading-4 text-slate-500">
              Téléchargement autorisé. Le dépôt, le remplacement et la suppression des fichiers courants restent indisponibles pour le rôle actif.
            </p>
          ) : null}

          <div className="import-table import-storage-table">
            <table className="min-w-full border-collapse text-[11px] text-slate-700" aria-label="Fichiers sources courants">
              <thead className="bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.9))]">
                <tr>
                  <th className="px-3 py-2 text-left text-[12px] font-semibold text-slate-950">Fichier</th>
                  <th className="px-3 py-2 text-left text-[12px] font-semibold text-slate-950">Usage</th>
                  <th className="px-3 py-2 text-left text-[12px] font-semibold text-slate-950">Version</th>
                  <th className="px-3 py-2 text-left text-[12px] font-semibold text-slate-950">Mis à jour</th>
                  <th className="px-3 py-2 text-right text-[12px] font-semibold text-slate-950">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {SOURCE_STORAGE_FIELD_CONFIGS.map((config) => (
                  <StoredSourceRow
                    key={config.field}
                    canManageSourceFilesByField={canManageSourceFilesByField}
                    config={config}
                    isSessionReady={isSessionReady}
                    isStorageAvailable={isStorageAvailable}
                    onDeleteCurrentFile={onDeleteCurrentFile}
                    onDownloadCurrentFile={onDownloadCurrentFile}
                    onUploadCurrentFile={onUploadCurrentFile}
                    sourceFileState={sourceStatesByField[config.field]}
                    storageProviderLabel={storageProviderLabel}
                    storageAccessReason={storageAccessReason}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </article>
  );
}

function ImportHistorySection({
  errorMessage,
  historyItems,
  isCollapsed,
  isLoading,
  isRefreshing,
  onToggleCollapsed,
}) {
  return (
    <article className="traceability-panel import-history rounded-[4px] border border-slate-200 bg-white shadow-sm">
      <div className="traceability-banner">
        <div className="traceability-heading">
          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? "Déplier la traçabilité des imports Excel" : "Replier la traçabilité des imports Excel"}
            className="traceability-toggle inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[4px] border border-slate-300 bg-white text-slate-700 transition-colors duration-150 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
          >
            <ChevronRight size={13} className={isCollapsed ? "" : "rotate-90"} />
          </button>
          <span className="traceability-icon" aria-hidden="true"><History size={24} /></span>
          <div className="traceability-copy">
            <p className="import-eyebrow">Traçabilité</p>
            <h2 className="import-card-title">{historyItems.length} derniers imports Excel</h2>
          </div>
        </div>

        <div className="traceability-actions">
          <span>{historyItems.length} import(s) récent(s)</span>
          <span className={joinClasses([
            "inline-flex items-center gap-1.5 rounded-[4px] px-2 py-0.5 text-[9px] font-semibold",
            isRefreshing ? statusToneClasses.info : statusToneClasses.neutral,
          ])}>
            {isRefreshing ? <LoaderCircle size={12} className="animate-spin" aria-hidden="true" /> : null}
            {isRefreshing ? "Actualisation…" : "Historique récent"}
          </span>
        </div>
      </div>

      {!isCollapsed ? (
        <div className="mt-2">
          {errorMessage ? (
            <p className={joinClasses([
              "rounded-[4px] border px-2.5 py-1.5 text-[11px] leading-4",
              statusToneClasses.warning,
            ])}>
              {errorMessage}
            </p>
          ) : null}

          {isLoading && historyItems.length === 0 ? (
            <p className="text-[11px] leading-5 text-slate-500">Lecture de la traçabilité des imports Excel...</p>
          ) : null}

          {!isLoading && historyItems.length === 0 ? (
            <p className="text-[11px] leading-5 text-slate-500">Aucun import appliqué n'a encore été enregistré pour cette page.</p>
          ) : null}

          {historyItems.length > 0 ? (
            <div className="import-table import-history-table">
              <table className="min-w-full border-collapse text-[11px] text-slate-700" aria-label="Traçabilité des imports Excel">
                <thead className="sticky top-0 z-10 bg-[var(--shell-control-pastille-fill,rgb(248_250_252/0.95))]">
                  <tr>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Date</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Élément</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Détail</th>
                    <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Impact</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {historyItems.map((entry) => {
                    const sourceLabel = resolveImportHistorySourceLabel(entry);
                    const actionLabel = resolveImportHistoryActionLabel(entry?.action);
                    const impactLabel = resolveImportHistoryImpactLabel(entry?.impact_level);
                    const actorLabel = typeof entry?.actor_email === "string" && entry.actor_email.trim()
                      ? entry.actor_email.trim()
                      : typeof entry?.actor_user_id === "string" && entry.actor_user_id.trim()
                        ? entry.actor_user_id.trim()
                        : "Compte système";
                    const summary = typeof entry?.summary === "string" && entry.summary.trim()
                      ? entry.summary.trim()
                      : `${sourceLabel} appliqué.`;
                    const dateLabel = `${formatImportHistoryTimestamp(entry?.created_at)} · ${actorLabel}`;

                    return (
                      <tr key={entry?.audit_id || `${sourceLabel}-${summary}`} className="odd:bg-slate-50/50">
                        <td data-label="Date" className="px-3 py-2 text-[10.5px] text-slate-500">
                          <span className="block" title={dateLabel}>{dateLabel}</span>
                        </td>
                        <td data-label="Élément" className="px-3 py-2">
                          <span className="block text-[11px] font-semibold text-slate-900" title={sourceLabel}>{sourceLabel}</span>
                        </td>
                        <td data-label="Détail" className="px-3 py-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <span className={joinClasses([
                              "inline-flex items-center rounded-[4px] px-2 py-0.75 text-[10px] font-semibold",
                              statusToneClasses.neutral,
                            ])}>
                              {actionLabel}
                            </span>
                            <span className="min-w-0 text-[11px] text-slate-600" title={summary}>{summary}</span>
                          </div>
                        </td>
                        <td data-label="Impact" className="px-3 py-2">
                          <span className={joinClasses([
                            "inline-flex items-center rounded-[4px] px-2 py-0.75 text-[10px] font-semibold uppercase tracking-[0.14em]",
                            resolveImportHistoryImpactToneClassName(entry?.impact_level),
                          ])}>
                            {impactLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

function ImportImpactPanel({
  confirmationActionsRef,
  bypassOrderImportGuards,
  canUseOrderImportGuardBypass,
  feedbackIssueCode,
  impact,
  isSubmitting,
  onConfirm,
  onDismiss,
  onRetry,
  submissionMode,
}) {
  const changedFields = Array.isArray(impact?.changed_fields)
    ? impact.changed_fields
      .map((field) => resolveResultLabel(field))
      .filter(Boolean)
    : [];
  const changeEntries = buildImpactChangeEntries(impact);
  const blockingEntries = filterRedundantBlockingEntries(buildImpactBlockingEntries(impact), changeEntries);
  const requiresConfirmation = Boolean(impact?.requires_confirmation);
  const isGuardBlocked = Boolean(impact?.guard_blocked || feedbackIssueCode === "import.guard_blocked");
  const hasRemovalEntries = hasImpactRemovals(changeEntries);
  const totalChangeValueCount = changeEntries.reduce(
    (count, entry) => count + (Array.isArray(entry.values) ? entry.values.length : 0),
    0,
  );
  const action = resolveImpactAction(impact, feedbackIssueCode, {
    bypassOrderImportGuards,
    canUseOrderImportGuardBypass,
    submissionMode,
  });
  const requiresBypassActivation = Boolean(isGuardBlocked && canUseOrderImportGuardBypass && !bypassOrderImportGuards);
  const shouldShowDismissAction = Boolean(action && (requiresConfirmation || isGuardBlocked));
  const shouldShowPrimaryAction = Boolean(action?.primaryActionLabel && (requiresConfirmation || isGuardBlocked));
  const shouldShowActionButtons = shouldShowDismissAction || shouldShowPrimaryAction;
  const shouldRenderActionButtonsInline = shouldShowActionButtons
    && changeEntries.length > 0
    && totalChangeValueCount <= 12;
  const primaryActionHandler = requiresConfirmation ? onConfirm : onRetry;
  const primaryActionDisabled = Boolean(isSubmitting || action?.primaryActionDisabled || !primaryActionHandler);

  const renderActionButtons = ({ compact = false } = {}) => (
    <div ref={confirmationActionsRef} tabIndex={-1} data-import-confirmation-actions="true" className={joinClasses([
      "flex items-center gap-2",
      "rounded-[4px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      compact ? "flex-wrap justify-end" : "flex-wrap sm:flex-nowrap",
    ])}>
      <HoverCard
        disabled={requiresConfirmation}
        placement="bottom-end"
        content={(
          <ActionHoverCardContent
            eyebrow="Alternative"
            title="Revenir à la sélection"
            description="Ferme ce conflit et garde vos fichiers choisis pour que vous puissiez les remplacer ou les effacer sans rien écrire sur le serveur."
          />
        )}
      >
        <span className="inline-flex shrink-0">
          <button
            type="button"
            onClick={onDismiss}
            disabled={isSubmitting}
            className={SECONDARY_BUTTON_CLASS_NAME}
          >
            Revenir à la sélection
          </button>
        </span>
      </HoverCard>

      {shouldShowPrimaryAction ? (
        <HoverCard
          disabled={requiresConfirmation}
          placement="bottom-end"
          content={(
            <ActionHoverCardContent
              eyebrow="Validation"
              title={action?.primaryActionLabel || "Confirmer l’import"}
              description={action?.primaryActionDescription || "Applique les nouveaux fichiers sur le serveur, sans supprimer les données absentes de votre sélection, puis invalide les vues dépendantes pour forcer un rechargement cohérent."}
            />
          )}
        >
          <span className="inline-flex shrink-0">
            <button
              type="button"
              onClick={primaryActionHandler || undefined}
              disabled={primaryActionDisabled}
              className={CONFIRM_BUTTON_CLASS_NAME}
            >
              {isSubmitting ? <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> : <ShieldCheck size={16} aria-hidden="true" />}
              {isSubmitting ? "Confirmation en cours…" : action?.primaryActionLabel || "Confirmer l’import"}
            </button>
          </span>
        </HoverCard>
      ) : null}
    </div>
  );

  return (
    <section className={joinClasses([
      "min-w-0 max-w-full overflow-hidden rounded-[4px] px-3 py-2.5",
      shellControlPastilleClassName,
    ])}>
      <div className="flex flex-wrap items-start justify-between gap-2.5">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Analyse d’impact</p>
          <p className="mt-1 text-[13px] font-semibold text-slate-950">Effet sur le planning actif</p>
          <p className="mt-1 text-[12px] leading-5 text-slate-600">{impact?.summary || "Un impact a été détecté sur les données actives."}</p>
        </div>

        {!requiresConfirmation && !isGuardBlocked ? (
          <span className={joinClasses([
            "inline-flex shrink-0 items-center gap-2 rounded-[4px] px-3 py-1.5 text-[11px] font-semibold",
            resolveImpactBadgeClassName(impact),
          ])}>
            <ShieldCheck size={14} aria-hidden="true" />
            {resolveImpactBadgeLabel(impact)}
          </span>
        ) : null}
      </div>

      {requiresBypassActivation ? (
        <div className="mt-2.5 rounded-[4px] border border-amber-200/80 bg-amber-50/80 px-3 py-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-800">Forçage requis</p>
          <p className="mt-1 text-[12px] leading-5 text-amber-950/90">
            Cochez « Forcer l’import des commandes (temporaire) » en haut de la page pour confirmer cet import.
          </p>
        </div>
      ) : null}

      <div className="mt-2.5 flex flex-wrap gap-2">
        {changedFields.map((label) => (
          <span
            key={label}
            className={joinClasses([
              "inline-flex shrink-0 items-center rounded-[4px] px-2.5 py-1 text-[11px] font-semibold",
              pillToneClasses.soft,
            ])}
          >
            Source: {label}
          </span>
        ))}
      </div>

      {action && !shouldRenderActionButtonsInline ? (
        <div className={joinClasses([
          "mt-2.5 flex flex-col gap-2 rounded-[4px] border px-3 py-2 lg:flex-row lg:items-center lg:justify-between",
          requiresConfirmation || isGuardBlocked ? "border-amber-200/80 bg-amber-50/80" : "border-slate-200/80 bg-white",
        ])}>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{action.label}</p>
            <p className="mt-1 text-[12px] leading-5 text-slate-700">{action.message}</p>
          </div>

          {shouldShowActionButtons && !shouldRenderActionButtonsInline ? (
            renderActionButtons()
          ) : null}
        </div>
      ) : null}

      {changeEntries.length > 0 ? (
        <div className="mt-2.5 grid gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Changements détectés</p>
            <p className="mt-1 text-[11px] leading-5 text-slate-500">
              {hasRemovalEntries
                ? "Les éléments ci-dessous seront ajoutés, modifiés ou retirés si vous lancez un remplacement complet."
                : "Les éléments ci-dessous seront ajoutés ou modifiés. Les retraits restent réservés à Remplacer toutes les données."}
            </p>
          </div>
          <div className="grid gap-2">
          {changeEntries.map((entry) => (
            <div key={entry.key} className="min-w-0 max-w-full rounded-[4px] border border-white bg-white px-3 py-2 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.16)]">
              <div className="flex min-w-0 flex-col gap-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">{entry.label}</p>
                <div className="min-w-0 flex flex-wrap gap-1.5">
                  {entry.values.map((value) => (
                    <span
                      key={value}
                      className="inline-flex max-w-full break-words rounded-[4px] bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.18)]"
                    >
                      {value}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
          </div>
          {shouldRenderActionButtonsInline ? (
            <div className="flex justify-end">
              {renderActionButtons({ compact: true })}
            </div>
          ) : null}
        </div>
      ) : null}

      {blockingEntries.length > 0 ? (
        <div className="mt-2.5 grid gap-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500">Éléments du planning touchés</p>
            <p className="mt-1 text-[11px] leading-5 text-slate-500">
              {requiresConfirmation
                ? "Ces éléments sont déjà référencés par le planning actif. C’est la raison de la confirmation demandée."
                : "Ces éléments sont déjà référencés par le planning actif."}
            </p>
          </div>
          <div className="grid gap-2">
          {blockingEntries.map((entry) => (
            <div key={entry.key} className="min-w-0 max-w-full rounded-[4px] border border-amber-200/70 bg-amber-50/70 px-3 py-2 shadow-[inset_0_0_0_1px_rgba(217,119,6,0.08)]">
              <div className="flex min-w-0 flex-col gap-1.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-800">{entry.label}</p>
                <div className="min-w-0 flex flex-wrap gap-1.5">
                {entry.values.map((value) => (
                  <span
                    key={value}
                    className="inline-flex max-w-full break-words rounded-[4px] bg-white px-2.5 py-1 text-[11px] font-medium text-amber-900 shadow-[inset_0_0_0_1px_rgba(217,119,6,0.18)]"
                  >
                    {value}
                  </span>
                ))}
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default function ImportFilesPage() {
  const { access, hasPermission } = useAccess();
  const confirmationActionsRef = React.useRef(null);
  const [bypassOrderImportGuards, setBypassOrderImportGuards] = React.useState(false);
  const [filesByField, setFilesByField] = React.useState(() => buildInitialFilesState());
  const [inputVersions, setInputVersions] = React.useState(() => buildInitialInputVersions());
  const [isImportHistoryCollapsed, setIsImportHistoryCollapsed] = React.useState(true);
  const [importHistoryItems, setImportHistoryItems] = React.useState([]);
  const [importHistoryErrorMessage, setImportHistoryErrorMessage] = React.useState("");
  const [isImportHistoryLoading, setIsImportHistoryLoading] = React.useState(true);
  const [isImportHistoryRefreshing, setIsImportHistoryRefreshing] = React.useState(false);
  const [sollioFile, setSollioFile] = React.useState(null);
  const [sollioInputVersion, setSollioInputVersion] = React.useState(0);
  const [isConvertingSollio, setIsConvertingSollio] = React.useState(false);
  const [sollioFeedback, setSollioFeedback] = React.useState(null);
  const [sollioSubmissionState, setSollioSubmissionState] = React.useState(() => createInitialImportSubmissionState());
  const [sollioDeliveryAddressChallenge, setSollioDeliveryAddressChallenge] = React.useState(null);
  const [isSollioDeliveryAddressDialogOpen, setIsSollioDeliveryAddressDialogOpen] = React.useState(false);
	const [sollioDeliveryAddressSelections, setSollioDeliveryAddressSelections] = React.useState({});
	const [isSollioAddressManagerOpen, setIsSollioAddressManagerOpen] = React.useState(false);
  const [uniagFile, setUniagFile] = React.useState(null);
  const [uniagInputVersion, setUniagInputVersion] = React.useState(0);
  const [isConvertingUniag, setIsConvertingUniag] = React.useState(false);
  const [uniagFeedback, setUniagFeedback] = React.useState(null);
  const [uniagSubmissionState, setUniagSubmissionState] = React.useState(() => createInitialImportSubmissionState());
  const sourceStorageFields = React.useMemo(() => SOURCE_STORAGE_FIELD_CONFIGS.map(({ field }) => field), []);

  const {
    handleDeleteSourceFile,
    handleDownloadSourceFile,
    handleUploadSourceFile,
    isSessionReady: isSourceStorageSessionReady,
    sourceStatesByField,
    storageAccess,
    storageRuntimeInfo,
  } = useSourceFilesStorage(sourceStorageFields);
  const visibleImportFieldConfigs = React.useMemo(() => IMPORT_FIELD_CONFIGS.filter(
    (config) => hasPermission(IMPORT_UPLOAD_PERMISSION_BY_FIELD[config.field]),
  ), [hasPermission]);
  const authorizedFilesByField = React.useMemo(() => Object.fromEntries(
    visibleImportFieldConfigs.map(({ field }) => [field, filesByField[field]]),
  ), [filesByField, visibleImportFieldConfigs]);
  const canManageSourceFilesByField = storageAccess.canManageSourceFilesByField || {};
  const canForceReplaceAll = canUseImportReplaceAll(access);
  const canUseOrderImportGuardBypass = canBypassOrderImportGuards(access, {
    isDev: typeof import.meta === "object" && Boolean(import.meta.env?.DEV),
  });
	const canManageSollioRules = hasPermission(ACCESS_PERMISSION_KEYS.dataManagerClientCorrespondencesWrite);

  const selectedFileEntries = React.useMemo(() => (
    visibleImportFieldConfigs.reduce((entries, config) => {
      const file = filesByField[config.field];
      if (!file) {
        return entries;
      }
      entries.push({
        filename: file.name,
        label: config.label,
      });
      return entries;
    }, [])
  ), [filesByField, visibleImportFieldConfigs]);
  const selectedFileCount = selectedFileEntries.length;

  const resetAllSelectedFiles = React.useCallback(() => {
    setFilesByField(buildInitialFilesState());
    setInputVersions((currentState) => IMPORT_FIELD_CONFIGS.reduce((nextState, config) => ({
      ...nextState,
      [config.field]: (currentState[config.field] || 0) + 1,
    }), {}));
  }, []);

  const loadImportHistory = React.useCallback(async ({ refresh = false, throwOnError = false } = {}) => {
    if (refresh) {
      setIsImportHistoryRefreshing(true);
    } else {
      setIsImportHistoryLoading(true);
    }

    setImportHistoryErrorMessage("");
    try {
      const nextItems = await fetchImportHistoryItems();
      setImportHistoryItems(nextItems);
    } catch (error) {
      setImportHistoryErrorMessage(resolveImportHistoryErrorMessage(error));
      if (!refresh) {
        setImportHistoryItems([]);
      }
      if (throwOnError) throw error;
    } finally {
      if (refresh) {
        setIsImportHistoryRefreshing(false);
      } else {
        setIsImportHistoryLoading(false);
      }
    }
  }, []);

  React.useEffect(() => {
    void loadImportHistory();
  }, [loadImportHistory]);

  const refreshImportHistory = React.useCallback(() => (
    loadImportHistory({ refresh: true, throwOnError: true })
  ), [loadImportHistory]);

  const {
    activeSubmissionMode,
    confirmationBottomSpace,
    clearServerStatus,
    feedback,
    hasServerStatus,
    impact,
    isSubmitting,
    results,
    submissionMode,
    submitImport,
  } = useImportSubmissionFlow({
    bypassOrderImportGuards,
    fieldConfigs: visibleImportFieldConfigs,
    filesByField: authorizedFilesByField,
    confirmationActionsRef,
    refreshImportHistory,
    resetAllSelectedFiles,
    resolveResultLabel,
    selectedFileCount,
  });

  const clearSollioSubmissionStatus = React.useCallback(() => {
    setSollioSubmissionState(createInitialImportSubmissionState());
  }, []);

  const clearUniagSubmissionStatus = React.useCallback(() => {
    setUniagSubmissionState(createInitialImportSubmissionState());
  }, []);

  const resetSollioDeliveryAddressSelection = React.useCallback(() => {
    setSollioDeliveryAddressChallenge(null);
    setIsSollioDeliveryAddressDialogOpen(false);
    setSollioDeliveryAddressSelections({});
  }, []);

  const clearSollioDeliveryAddressSelections = React.useCallback(() => {
		setSollioDeliveryAddressChallenge(null);
    setSollioDeliveryAddressSelections({});
    setIsSollioDeliveryAddressDialogOpen(false);
  }, []);

  const clearConverterSubmissionStatus = React.useCallback(() => {
    clearSollioSubmissionStatus();
    clearUniagSubmissionStatus();
  }, [clearSollioSubmissionStatus, clearUniagSubmissionStatus]);

  const clearDisplayedServerStatus = React.useCallback(() => {
    clearServerStatus();
    clearConverterSubmissionStatus();
  }, [clearConverterSubmissionStatus, clearServerStatus]);

  const submitImportWithClearedSollioStatus = React.useCallback(async (options) => {
    clearConverterSubmissionStatus();
    return submitImport(options);
  }, [clearConverterSubmissionStatus, submitImport]);

  const hasSollioServerStatus = Boolean(sollioSubmissionState.feedback || sollioSubmissionState.results.length > 0);
  const hasUniagServerStatus = Boolean(uniagSubmissionState.feedback || uniagSubmissionState.results.length > 0);
  const converterSubmissionState = hasUniagServerStatus
    ? uniagSubmissionState
    : (hasSollioServerStatus ? sollioSubmissionState : null);
  const displayedFeedback = converterSubmissionState ? converterSubmissionState.feedback : feedback;
  const displayedImpact = converterSubmissionState ? converterSubmissionState.impact : impact;
  const displayedResults = converterSubmissionState ? converterSubmissionState.results : results;
  const hasDisplayedServerStatus = Boolean(converterSubmissionState) || hasServerStatus;

  const isPrimaryActionDisabled = isImportActionDisabled({
    isSubmitting,
    selectedFileCount,
  });
  const isSelectionSubmitting = isSubmitting && activeSubmissionMode !== "force";
  const isForceReplaceSubmitting = isSubmitting && activeSubmissionMode === "force";

  const handleFileSelect = React.useCallback((field, nextFile) => {
    setFilesByField((currentState) => ({
      ...currentState,
      [field]: nextFile,
    }));
    clearDisplayedServerStatus();
  }, [clearDisplayedServerStatus]);

  const clearSelectedFile = React.useCallback((field) => {
    setFilesByField((currentState) => ({
      ...currentState,
      [field]: null,
    }));
    setInputVersions((currentState) => ({
      ...currentState,
      [field]: (currentState[field] || 0) + 1,
    }));
    clearDisplayedServerStatus();
  }, [clearDisplayedServerStatus]);

  const handleBypassOrderImportGuardsChange = React.useCallback((event) => {
    setBypassOrderImportGuards(Boolean(event?.target?.checked));
  }, []);

  const clearSollioFile = React.useCallback(() => {
    setSollioFile(null);
    setSollioInputVersion((currentValue) => currentValue + 1);
    setSollioFeedback(null);
    clearSollioSubmissionStatus();
    resetSollioDeliveryAddressSelection();
		setIsSollioAddressManagerOpen(false);
  }, [clearSollioSubmissionStatus, resetSollioDeliveryAddressSelection]);

  const handleSollioFileSelect = React.useCallback((nextFile) => {
    setSollioFile(nextFile);
    setSollioFeedback(null);
    clearSollioSubmissionStatus();
    resetSollioDeliveryAddressSelection();
		setIsSollioAddressManagerOpen(false);
  }, [clearSollioSubmissionStatus, resetSollioDeliveryAddressSelection]);

  const handleSollioConversion = React.useCallback(async () => {
    if (!sollioFile || isConvertingSollio) {
      return;
    }

    if (
      sollioDeliveryAddressChallenge
      && !isSollioDeliveryAddressSelectionComplete(
        sollioDeliveryAddressChallenge,
        sollioDeliveryAddressSelections,
      )
    ) {
      setIsSollioDeliveryAddressDialogOpen(true);
      return;
    }

    setIsConvertingSollio(true);
    setSollioFeedback(null);
    clearConverterSubmissionStatus();
    try {
		const explicitSelections = sollioDeliveryAddressChallenge
			? buildSollioDeliveryAddressSelections(
            sollioDeliveryAddressChallenge,
            sollioDeliveryAddressSelections,
			)
			: [];
		const download = explicitSelections.length > 0
			? await convertSollioToOrders({
				deliveryAddressSelections: explicitSelections,
				file: sollioFile,
			})
			: await convertSollioToOrders({ file: sollioFile });
      invalidateDataManagerStateCache();
		const reusedCount = download?.reusedSelectionCount || 0;
		const savedCount = download?.savedSelectionCount || 0;
      setSollioFeedback({
        tone: "success",
			message: `Conversion terminée. ${reusedCount} adresse(s) réutilisée(s), ${savedCount} nouveau(x) choix mémorisé(s). ${download?.fileName || "Orders_Sollio.xlsx"} a été préparé pour ${download?.destinationLabel || "le téléchargement navigateur"}.`,
      });
      clearConverterSubmissionStatus();
      resetSollioDeliveryAddressSelection();
      setSollioFile(null);
      setSollioInputVersion((currentValue) => currentValue + 1);
		setIsSollioAddressManagerOpen(false);
    } catch (error) {
      const nextChallenge = error?.status === 409
        && error?.code === "sollio_delivery_address_selection_required"
        ? normalizeSollioDeliveryAddressChallenge(error?.payload)
        : null;

      if (nextChallenge) {
        setSollioDeliveryAddressChallenge(nextChallenge);
				setSollioDeliveryAddressSelections(
					buildInitialSollioDeliveryAddressSelections(nextChallenge),
				);
        setIsSollioDeliveryAddressDialogOpen(true);
        return;
      }

      const errorMessage = error?.message || "La conversion Sollio a échoué.";
      setSollioFeedback({
        tone: "danger",
        message: errorMessage,
      });
      setSollioSubmissionState(createSollioConversionErrorSubmissionState({
        fileName: typeof sollioFile?.name === "string" ? sollioFile.name : "",
        message: errorMessage,
      }));
    } finally {
      setIsConvertingSollio(false);
    }
  }, [
    clearConverterSubmissionStatus,
    isConvertingSollio,
    resetSollioDeliveryAddressSelection,
    sollioDeliveryAddressChallenge,
    sollioDeliveryAddressSelections,
    sollioFile,
  ]);

  const handleSollioDeliveryAddressSelectionChange = React.useCallback((orderKey, correspondenceId) => {
    setSollioDeliveryAddressSelections((currentSelections) => {
      if (!correspondenceId) {
        const nextSelections = { ...currentSelections };
        delete nextSelections[orderKey];
        return nextSelections;
      }

      return {
        ...currentSelections,
        [orderKey]: correspondenceId,
      };
    });
  }, []);

  const handleCloseSollioDeliveryAddressDialog = React.useCallback(() => {
    setIsSollioDeliveryAddressDialogOpen(false);
  }, []);

	const handleOpenSollioAddressManager = React.useCallback(() => {
		setIsSollioAddressManagerOpen(true);
	}, []);

  const clearUniagFile = React.useCallback(() => {
    setUniagFile(null);
    setUniagInputVersion((currentValue) => currentValue + 1);
    setUniagFeedback(null);
    clearUniagSubmissionStatus();
  }, [clearUniagSubmissionStatus]);

  const handleUniagFileSelect = React.useCallback((nextFile) => {
    setUniagFile(nextFile);
    setUniagFeedback(null);
    clearUniagSubmissionStatus();
  }, [clearUniagSubmissionStatus]);

  const handleUniagConversion = React.useCallback(async () => {
    if (!uniagFile || isConvertingUniag) {
      return;
    }

    setIsConvertingUniag(true);
    setUniagFeedback(null);
    clearConverterSubmissionStatus();
    try {
      const download = await convertUniagToOrders({ file: uniagFile });
      invalidateDataManagerStateCache();
      setUniagFeedback({
        tone: "success",
        message: `Conversion terminée. ${download?.fileName || "Orders_Uniag.xlsx"} a été préparé pour ${download?.destinationLabel || "le téléchargement navigateur"}.`,
      });
      clearConverterSubmissionStatus();
      setUniagFile(null);
      setUniagInputVersion((currentValue) => currentValue + 1);
    } catch (error) {
      const errorMessage = error?.message || "La conversion Uniag a échoué.";
      setUniagFeedback({
        tone: "danger",
        message: errorMessage,
      });
      setUniagSubmissionState(createUniagConversionErrorSubmissionState({
        fileName: typeof uniagFile?.name === "string" ? uniagFile.name : "",
        message: errorMessage,
      }));
    } finally {
      setIsConvertingUniag(false);
    }
  }, [clearConverterSubmissionStatus, isConvertingUniag, uniagFile]);

  // Les deux reprennent le mode de l'envoi affiché : un remplacement complet reste un remplacement.
  const handleConfirmBreakingImport = React.useCallback(() => {
    void submitImportWithClearedSollioStatus({ confirmBreakingImport: true, submissionMode });
  }, [submissionMode, submitImportWithClearedSollioStatus]);

  const handleRetryImport = React.useCallback(() => {
    void submitImportWithClearedSollioStatus({ submissionMode });
  }, [submissionMode, submitImportWithClearedSollioStatus]);

  const {
    confirmationDialog,
    handleForceReplaceImport,
    handleSubmit,
  } = useImportConfirmationDialog({
    isSubmitting,
    selectedFileCount,
    selectedFileEntries,
    submitImport: submitImportWithClearedSollioStatus,
  });

  return (
    <section className={`import-files-page mx-auto w-full min-w-0 ${IMPORT_SURFACE_WIDTH_CLASS_NAME} px-4 pb-4 pt-0`}>
      <OrderDuplicateConfirmationDialog />
      <div className="import-content">
        <WorkspacePageHeader
          className="import-hero"
          icon={Upload}
          title="Import fichiers"
          titleAs="h1"
          description={<span className="import-hero-subtitle">Importer les fichiers Excel de commandes, camions, remorques, chauffeurs, clients et codes produit.</span>}
        />
        {canUseOrderImportGuardBypass ? (
          <div className="import-bypass flex flex-wrap items-start justify-between gap-3 rounded-[4px] border border-amber-200/80 bg-amber-50 p-3">
            <label className="flex min-w-0 flex-1 items-start gap-2.5 text-left">
              <input
                type="checkbox"
                checked={bypassOrderImportGuards}
                onChange={handleBypassOrderImportGuardsChange}
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-2 focus:ring-amber-400/70"
              />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold text-amber-950">Forcer l’import des commandes (temporaire)</span>
                <span className="mt-0.5 block text-[12px] leading-5 text-amber-900/85">
                  Autorise temporairement l’import des commandes même si la journée est clôturée, si la livraison est déjà confirmée ou si la date est passée.
                </span>
              </span>
            </label>
            <span className={joinClasses([
              "inline-flex items-center gap-1.5 rounded-[4px] px-2.5 py-1 text-[10px] font-semibold",
              bypassOrderImportGuards ? statusToneClasses.warning : statusToneClasses.neutral,
            ])}>
              <ShieldCheck size={14} aria-hidden="true" />
              {bypassOrderImportGuards ? "Forçage actif" : "Forçage inactif"}
            </span>
          </div>
        ) : null}
      {visibleImportFieldConfigs.length > 0 ? (
      <section className="import-card import-selection" aria-labelledby="import-selection-heading">
        <div className="import-section-heading">
          <h2 id="import-selection-heading" className="import-card-title import-selection-title">Fichiers à importer</h2>
          <div className="import-selection-actions">
              <HoverCard
                placement="bottom"
                content={(
                  <ImportButtonHoverCardContent
                    actionLabel="Importer la sélection"
                    description="Envoie uniquement les fichiers choisis, analyse l'impact métier puis ajoute ou met à jour les données sans supprimer les éléments absents de la sélection."
                    disabledDescription="Sélectionnez au moins un fichier dans les cartes disponibles pour activer l'import."
                    isSubmitting={isSubmitting}
                    selectedFileCount={selectedFileCount}
                  />
                )}
              >
                <span className="inline-flex">
                  <button
                    type="submit"
                    form="import-files-form"
                    disabled={isPrimaryActionDisabled}
                    className={PRIMARY_BUTTON_CLASS_NAME}
                  >
                    {isSelectionSubmitting ? <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> : <Upload size={16} aria-hidden="true" />}
                    {isSelectionSubmitting ? "Import en cours…" : "Importer la sélection"}
                  </button>
                </span>
              </HoverCard>

              {SHOW_FORCE_REPLACE_ACTION && canForceReplaceAll ? (
                <HoverCard
                  placement="bottom"
                  content={(
                    <ImportButtonHoverCardContent
                      actionLabel="Remplacer toutes les données"
                      description="Force l'écriture des fichiers choisis une fois confirmé, efface le planning courant lié puis repart d'un état vierge à réoptimiser."
                      disabledDescription="Sélectionnez au moins un fichier pour pouvoir lancer un remplacement complet."
                      isSubmitting={isSubmitting}
                      selectedFileCount={selectedFileCount}
                    />
                  )}
                >
                  <span className="inline-flex">
                    <button
                      type="button"
                      disabled={isPrimaryActionDisabled}
                      onClick={handleForceReplaceImport}
                      className={FORCE_REPLACE_BUTTON_CLASS_NAME}
                    >
                      {isForceReplaceSubmitting ? <LoaderCircle size={16} className="animate-spin" aria-hidden="true" /> : <ShieldCheck size={16} aria-hidden="true" />}
                      {isForceReplaceSubmitting ? "Remplacement en cours…" : "Remplacer toutes les données"}
                    </button>
                  </span>
                </HoverCard>
              ) : null}
          </div>
        </div>
      {/* Six sources : deux rangées de trois plutôt qu'une carte seule sur la deuxième rangée. */}
      <form id="import-files-form" className="import-source-grid" style={{ "--import-source-count": visibleImportFieldConfigs.length > 5 ? Math.ceil(visibleImportFieldConfigs.length / 2) : visibleImportFieldConfigs.length }} onSubmit={handleSubmit}>
        {visibleImportFieldConfigs.map((config) => (
          <ImportZone
            key={config.field}
            config={config}
            file={filesByField[config.field]}
            inputVersion={inputVersions[config.field]}
            isSubmitting={isSubmitting}
            onClear={clearSelectedFile}
            onSelect={handleFileSelect}
          />
        ))}
      </form>
      </section>
      ) : null}

      <div className="import-converters">
      <SollioConversionPanel
        feedback={sollioFeedback}
        file={sollioFile}
        inputVersion={sollioInputVersion}
        isConverting={isConvertingSollio}
		manageActionLabel="Gérer les clients à adresses variables"
        onClear={clearSollioFile}
        onConvert={handleSollioConversion}
		onManage={handleOpenSollioAddressManager}
        onSelect={handleSollioFileSelect}
      />
      <UniagConversionPanel
        feedback={uniagFeedback}
        file={uniagFile}
        inputVersion={uniagInputVersion}
        isConverting={isConvertingUniag}
        onClear={clearUniagFile}
        onConvert={handleUniagConversion}
        onSelect={handleUniagFileSelect}
      />
      </div>

		<SollioDeliveryAddressManagerDialog
			canManageRules={canManageSollioRules}
			isOpen={isSollioAddressManagerOpen}
			onClose={() => setIsSollioAddressManagerOpen(false)}
		/>

      <SollioDeliveryAddressSelectionDialog
        challenge={sollioDeliveryAddressChallenge}
        isConverting={isConvertingSollio}
        isOpen={isSollioDeliveryAddressDialogOpen}
        onCancel={clearSollioDeliveryAddressSelections}
        onClose={handleCloseSollioDeliveryAddressDialog}
        onConfirm={handleSollioConversion}
        onSelectionChange={handleSollioDeliveryAddressSelectionChange}
        selections={sollioDeliveryAddressSelections}
      />

      <StoredSourcesSection
        canManageSourceFilesByField={canManageSourceFilesByField}
        isSessionReady={isSourceStorageSessionReady}
        isStorageAvailable={storageAccess.isAvailable}
        onDeleteCurrentFile={handleDeleteSourceFile}
        onDownloadCurrentFile={handleDownloadSourceFile}
        onUploadCurrentFile={handleUploadSourceFile}
        sourceStatesByField={sourceStatesByField}
        storageProviderLabel={storageRuntimeInfo?.label}
        storageAccessReason={storageAccess.reason}
      />

      <ImportHistorySection
        errorMessage={importHistoryErrorMessage}
        historyItems={importHistoryItems}
        isCollapsed={isImportHistoryCollapsed}
        isLoading={isImportHistoryLoading}
        isRefreshing={isImportHistoryRefreshing}
        onToggleCollapsed={() => setIsImportHistoryCollapsed((currentValue) => !currentValue)}
      />

      {hasDisplayedServerStatus ? (
      <article aria-labelledby="import-feedback-heading" className="min-w-0 max-w-full overflow-hidden rounded-[4px] border border-black/6 bg-white px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.045)]">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Statut serveur</p>
            <h2 id="import-feedback-heading" className="mt-1 text-[0.96rem] font-semibold text-slate-950">Dernier retour d’import</h2>
          </div>
          <span className={joinClasses([
            "inline-flex items-center gap-2 rounded-[4px] px-3 py-1.5 text-[11px] font-semibold",
            resolveFeedbackToneClassName(displayedFeedback?.tone),
          ])}>
            {displayedFeedback?.tone === "success" ? <CheckCircle2 size={14} aria-hidden="true" /> : <ShieldCheck size={14} aria-hidden="true" />}
            {resolveFeedbackSummary(displayedFeedback, displayedImpact)}
          </span>
        </div>

        {displayedImpact ? (
          <div className="mt-3">
            <ImportImpactPanel
              confirmationActionsRef={confirmationActionsRef}
              bypassOrderImportGuards={bypassOrderImportGuards}
              canUseOrderImportGuardBypass={canUseOrderImportGuardBypass}
              feedbackIssueCode={displayedFeedback?.issue?.code}
              impact={displayedImpact}
              isSubmitting={isSubmitting}
              onConfirm={handleConfirmBreakingImport}
              onDismiss={clearDisplayedServerStatus}
              onRetry={handleRetryImport}
              submissionMode={submissionMode}
            />
          </div>
        ) : null}

        <div className="mt-2.5 grid gap-2">
          {displayedResults.map((result, index) => {
            const resultStatus = resolveImportResultStatusState({ feedback: displayedFeedback, impact: displayedImpact, result });
            const label = resolveResultLabel(result?.field);

            return (
              <div
                key={`${result?.field || "request"}-${index}`}
                className={joinClasses([
                  "rounded-[4px] px-3 py-2",
                  shellControlPastilleClassName,
                ])}
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[12px] font-semibold text-slate-950">{label}</span>
                      <span className={joinClasses([
                        "inline-flex items-center rounded-[4px] px-2.5 py-1 text-[11px] font-semibold",
                        resultStatus.className,
                      ])}>
                        {resultStatus.label}
                      </span>
                    </div>
                    <p className="mt-1 text-[12px] leading-5 text-slate-600">{result?.message || "Aucun message retourné."}</p>
                  </div>

                  {result?.filename ? (
                    <span className="max-w-full break-words rounded-[4px] bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600 shadow-[inset_0_0_0_1px_rgba(148,163,184,0.18)]">
                      {result.filename}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

      </article>
      ) : null}

      {confirmationBottomSpace > 0 ? (
        <div aria-hidden="true" data-import-confirmation-spacer="true" style={{ height: confirmationBottomSpace }} />
      ) : null}
      {confirmationDialog ? <ConfirmationModal {...confirmationDialog} /> : null}
      </div>
    </section>
  );
}
